import { z } from "zod";
import { type LoginResponse, loginPasswordSchema, userIdSchema } from "./auth";
import { emailSchema, type MfaMethod } from "./platform";

/**
 * System admin sign-in (Eduleo's own team, at /system-admin/login) and two-step verification
 * (MFA). Schools sign in at /login with their Institution ID; the team signs in with only a
 * User ID and password, then a code when they have turned MFA on.
 */

export const MFA_METHODS = ["totp", "email"] as const satisfies readonly MfaMethod[];

export const MFA_METHOD_LABELS: Record<MfaMethod, string> = {
  totp: "Authenticator app",
  email: "Code by email",
};

/** Recovery codes given out when an authenticator app is set up. Each works once. */
export const RECOVERY_CODE_COUNT = 10;

export const systemAdminLoginSchema = z.object({
  userId: userIdSchema,
  password: loginPasswordSchema,
});

export type SystemAdminLogin = z.output<typeof systemAdminLoginSchema>;

/** Opaque handle for one pending code check. */
const challengeSchema = z
  .string()
  .min(20, { error: "Start again" })
  .max(200, { error: "Start again" })
  .regex(/^[A-Za-z0-9_.-]+$/, { error: "Start again" });

export const mfaCodeSchema = z
  .string()
  .trim()
  .transform((value) => value.replace(/\s+/g, ""))
  .pipe(z.string().regex(/^\d{6}$/, { error: "Enter the 6-digit code" }));

const RECOVERY_CODE = /^[a-z0-9]{5}-?[a-z0-9]{5}$/;

/** At sign-in: the 6-digit code, or one recovery code ("abcde-12345") instead. */
export const mfaSignInCodeSchema = z
  .string()
  .trim()
  .toLowerCase()
  .transform((value) => value.replace(/\s+/g, ""))
  .pipe(
    z.string().refine((value) => /^\d{6}$/.test(value) || RECOVERY_CODE.test(value), {
      error: "Enter the 6-digit code or a recovery code",
    }),
  );

export function isRecoveryCode(code: string): boolean {
  return RECOVERY_CODE.test(code);
}

export const mfaVerifySchema = z.object({ challenge: challengeSchema, code: mfaSignInCodeSchema });
export const mfaResendSchema = z.object({ challenge: challengeSchema });

/** Password check before changing your own sign-in security. */
export const mfaPasswordSchema = z.object({ password: loginPasswordSchema });
export const mfaEmailStartSchema = z.object({ password: loginPasswordSchema, email: emailSchema });
export const mfaConfirmSchema = z.object({ challenge: challengeSchema, code: mfaCodeSchema });

/** Answer to a correct password when MFA is on: send the code to /system-admin/mfa next. */
export interface MfaChallengeResponse {
  mfaRequired: true;
  challenge: string;
  method: MfaMethod;
  /** Masked email the code went to ("a•••@eduleo.in"); null for an authenticator app. */
  sentTo: string | null;
  /** Seconds until this sign-in attempt runs out and the person must start again. */
  expiresIn: number;
}

export type SystemAdminLoginResponse = LoginResponse | MfaChallengeResponse;

export function isMfaChallenge(value: SystemAdminLoginResponse): value is MfaChallengeResponse {
  return "mfaRequired" in value && value.mfaRequired === true;
}

export interface MfaResendResponse {
  sentTo: string;
  expiresIn: number;
}

/** Your own two-step verification, as the Security page shows it. */
export interface MfaStatusDto {
  enabled: boolean;
  method: MfaMethod | null;
  /** The address codes go to (email method only). */
  email: string | null;
  enabledAt: string | null;
  /** Unused recovery codes (authenticator app only). */
  recoveryCodesLeft: number;
  /** False until Eduleo has set up sending email; then only the authenticator app is offered. */
  emailAvailable: boolean;
}

export interface MfaTotpSetupDto {
  challenge: string;
  /** Base32 secret, for typing into the app when the QR code can't be scanned. */
  secret: string;
  /** otpauth:// link encoded in the QR code. */
  otpauthUri: string;
  expiresIn: number;
}

export interface MfaEmailSetupDto {
  challenge: string;
  sentTo: string;
  expiresIn: number;
}

export interface MfaConfirmDto {
  status: MfaStatusDto;
  /** Shown once, right after an authenticator app is set up. Null for email. */
  recoveryCodes: string[] | null;
}

export interface RecoveryCodesDto {
  recoveryCodes: string[];
}

/** "amandeep@eduleo.in" -> "a•••@eduleo.in". Enough to recognise, not enough to collect. */
export function maskEmail(email: string): string {
  const at = email.lastIndexOf("@");
  if (at < 1) return "•••";
  return `${email.charAt(0)}•••${email.slice(at)}`;
}
